import { Grid } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { FullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { useAliases } from '../../../api/alias/hooks';
import { DataValue } from '../../../api/types';
import { toRegularMetricResultOrError } from '../../../api/utils';
import { UNDEFINED_DISPLAY_KEY } from '../../../constants';
import {
  useAliasServiceContext,
  useGlobalLocaleContext,
  useLatestDomainPreferencesContext,
  useMetricDetailsMapContext,
  useMetricServiceContext,
} from '../../../context/contexts';
import { GlobalDisplayState } from '../../../context/types';
import { DataFieldWithDataType, RegularMetricIdType, TimeSelection } from '../../../types';
import { CHART_CONTENT_HEIGHT, CHART_TITLE_HEIGHT } from '../../styles';
import { ManualDataRefetchCountHandle, MetricTypes } from '../../types';
import { getFilterConditions } from '../../utils';
import { ChartDataFetchError } from '../charts/ChartDataFetchError';
import { ChartTitle } from '../charts/ChartTitle';
import { toMuiChartsTimePeriodData } from '../charts/mui-charts/utils-display';
import { useDisplayWithPersistence } from '../charts/timeperiod/hooks';
import { timeSliderStateToTimePeriodTimeSelection } from '../charts/timeperiod/utils';
import { TableViewTimePeriodComponentConfig } from '../dashboards/types';
import { Filter, Segment } from '../filter/filterbar/types';
import { ItemsState } from '../filter/types';
import { formatHierarchicalLabel } from '../filter/utils-display';
import { TimeSliderState } from '../timeslider/types';
import { TableViewMetrics } from './TableViewMetrics';
import { Tools } from './Tools';

interface TableViewTimePeriodProps {
  componentConfig: TableViewTimePeriodComponentConfig;
  manualDataRefetchCountHandle: ManualDataRefetchCountHandle;
  timeSliderState: TimeSliderState | null;
  filtersState: ItemsState<Filter>;
  segmentationState: ItemsState<Segment>;
  showTools: boolean;
  fullScreenHandle: FullScreenHandle;
}

export const TableViewTimePeriod = (props: TableViewTimePeriodProps) => {
  const {
    componentConfig,
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
    segmentationState,
    showTools,
    fullScreenHandle,
  } = props;
  const { t } = useTranslation();
  const metricService = useMetricServiceContext();

  const { title, query, chartTypeConfig } = componentConfig;
  const { metric, timeSelection, filters, segmentations } = query;
  const finalTimeSelection: TimeSelection = timeSliderState
    ? timeSliderStateToTimePeriodTimeSelection(timeSliderState)
    : timeSelection;

  const allFilters =
    [getFilterConditions(filtersState.items, MetricTypes.REGULAR, false), filters]
      .flatMap((f) => (f ? [f] : []))
      .join(' AND ') || undefined;

  const [queryKey] = useDebounce(
    JSON.stringify([
      'table-view-time-period',
      metric,
      finalTimeSelection,
      allFilters,
      segmentations,
      manualDataRefetchCountHandle.current,
    ]),
    500
  );

  const metricDetailsMap = useMetricDetailsMapContext();
  const aliasService = useAliasServiceContext();
  const domainPreferences = useLatestDomainPreferencesContext();
  const { data: aliases } = useAliases(aliasService);
  const locale = useGlobalLocaleContext();
  const getAliasForMetric = aliasService.getAliasForMetricGroupId(aliases, locale.selected);
  const formatLabel = (value: DataValue[], dataField: DataFieldWithDataType) =>
    formatHierarchicalLabel(value, dataField, t(UNDEFINED_DISPLAY_KEY), t);

  const { data, error } = useSuspenseQuery({
    queryKey: [queryKey],
    queryFn: () => {
      return toRegularMetricResultOrError(
        metricService.queryRegularMetrics,
        [metric] as RegularMetricIdType[],
        finalTimeSelection,
        allFilters,
        segmentations
      );
    },
  });

  if (error) {
    return <ChartDataFetchError manualDataRefetchCountHandle={manualDataRefetchCountHandle} />;
  }

  const dummyGlobalDisplayState: GlobalDisplayState = {
    enablePercentage: false,
    showPercentage: false,
    enableStack: false,
    showStack: false,
    enableGridLines: false,
    showGridLines: false,
    enableLabels: false,
    showLabels: false,
    enableLegend: false,
    showLegend: false,
  };

  const displayHandle = useDisplayWithPersistence(
    componentConfig.chartId,
    chartTypeConfig,
    segmentations,
    [metric],
    dummyGlobalDisplayState
  );

  const [displayState] = displayHandle;

  const { tableViewData, metricSql } = toMuiChartsTimePeriodData(
    data,
    segmentations,
    displayState,
    metricDetailsMap,
    formatLabel,
    getAliasForMetric,
    undefined,
    domainPreferences.settings,
    t,
    undefined
  );

  return (
    <Grid item container direction="column" sx={{ height: '100%' }}>
      <Grid item container sx={{ height: CHART_TITLE_HEIGHT }}>
        <ChartTitle
          metrics={[metric]}
          metricsSql={metricSql}
          title={title?.[locale.selected]}
          timeSelection={finalTimeSelection}
          segmentations={segmentations}
          userFilters={allFilters}
        />
        {showTools ? <Tools fullScreenHandle={fullScreenHandle} /> : null}
      </Grid>
      <Grid item container sx={{ height: CHART_CONTENT_HEIGHT }}>
        <TableViewMetrics
          tableData={tableViewData}
          fullScreenHandle={fullScreenHandle}
          chartHeight={CHART_CONTENT_HEIGHT}
        />
      </Grid>
    </Grid>
  );
};
